import apiClient from './client';
import {
  Clinic,
  ClinicMembership,
  ClinicInvitation,
  ClinicWorkingDay,
  ClinicTimeBlock,
  ClinicBlockedDate,
  ClinicAppointment,
  ClinicAvailabilityDay,
  ClinicRole,
  ClinicVisibility
} from '../types';

// ============================================================================
// CLINIC CRUD
// ============================================================================

export interface ClinicListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Clinic[];
}

export interface ClinicFilters {
  search?: string;
  city?: string;
  profession?: string;
}

export const getClinics = async (filters?: ClinicFilters, page: number = 1): Promise<ClinicListResponse> => {
  const params = new URLSearchParams();
  if (filters?.search) params.append('search', filters.search);
  if (filters?.city) params.append('city', filters.city);
  if (filters?.profession) params.append('profession', filters.profession);
  params.append('page', page.toString());
  
  const response = await apiClient.get(`/clinics/?${params.toString()}`);
  return response.data;
};

export const getClinicById = async (id: number): Promise<Clinic> => {
  const response = await apiClient.get(`/clinics/${id}/`);
  return response.data;
};

export interface CreateClinicData {
  name: string;
  description?: string;
  profile_photo?: File;
  phone?: string;
  email?: string;
  practice_address?: string;
  location_description?: string;
  latitude?: number;
  longitude?: number;
  subscription_plan?: ClinicPlan;
}

export const createClinic = async (data: CreateClinicData): Promise<Clinic> => {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (value instanceof File) {
        formData.append(key, value);
      } else {
        formData.append(key, String(value));
      }
    }
  });
  
  const response = await apiClient.post('/clinics/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const updateClinic = async (id: number, data: Partial<CreateClinicData & { active?: boolean; visibility?: ClinicVisibility }>): Promise<Clinic> => {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (value instanceof File) {
        formData.append(key, value);
      } else {
        formData.append(key, String(value));
      }
    }
  });
  
  const response = await apiClient.patch(`/clinics/${id}/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deleteClinic = async (id: number): Promise<void> => {
  await apiClient.delete(`/clinics/${id}/`);
};

export const getClinicAvailability = async (
  clinicId: number,
  professionalId: number,
  options?: { date?: string; startDate?: string; endDate?: string }
): Promise<ClinicAvailabilityDay[]> => {
  const params = new URLSearchParams();
  params.append('professional_id', professionalId.toString());
  if (options?.date) params.append('date', options.date);
  if (options?.startDate) params.append('start_date', options.startDate);
  if (options?.endDate) params.append('end_date', options.endDate);
  
  const response = await apiClient.get(`/clinics/${clinicId}/availability/?${params.toString()}`);
  return response.data;
};

export const getMyClinicMembership = async (clinicId: number): Promise<ClinicMembership> => {
  const response = await apiClient.get(`/clinics/${clinicId}/my_membership/`);
  return response.data;
};

// ============================================================================
// CLINIC MEMBERSHIPS
// ============================================================================

export interface MembershipListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ClinicMembership[];
}

export const getClinicMemberships = async (clinicId: number): Promise<MembershipListResponse> => {
  const response = await apiClient.get(`/clinic-memberships/?clinic=${clinicId}`);
  return response.data;
};

export const getMyClinicMemberships = async (): Promise<MembershipListResponse> => {
  const response = await apiClient.get('/clinic-memberships/my/');
  return response.data;
};

export const updateMembershipRole = async (
  membershipId: number,
  data: { role?: ClinicRole; is_active?: boolean }
): Promise<ClinicMembership> => {
  const response = await apiClient.patch(`/clinic-memberships/${membershipId}/`, data);
  return response.data;
};

export const removeMembership = async (membershipId: number): Promise<void> => {
  await apiClient.delete(`/clinic-memberships/${membershipId}/`);
};

// ============================================================================
// CLINIC INVITATIONS
// ============================================================================

export interface InvitationListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ClinicInvitation[];
}

export const getClinicInvitations = async (clinicId?: number): Promise<InvitationListResponse> => {
  const params = clinicId ? `?clinic=${clinicId}` : '';
  const response = await apiClient.get(`/clinic-invitations/${params}`);
  return response.data;
};

export const getMyInvitations = async (): Promise<InvitationListResponse> => {
  const response = await apiClient.get('/clinic-invitations/');
  return response.data;
};

export interface CreateInvitationData {
  clinic: number;
  invited_email: string;
  role: ClinicRole;
  message?: string;
}

export const createClinicInvitation = async (data: CreateInvitationData): Promise<ClinicInvitation> => {
  const response = await apiClient.post('/clinic-invitations/', data);
  return response.data;
};

export const acceptInvitation = async (invitationId: number): Promise<{ message: string; membership: ClinicMembership }> => {
  const response = await apiClient.post(`/clinic-invitations/${invitationId}/accept/`);
  return response.data;
};

export const declineInvitation = async (invitationId: number): Promise<{ message: string }> => {
  const response = await apiClient.post(`/clinic-invitations/${invitationId}/decline/`);
  return response.data;
};

export const deleteClinicInvitation = async (invitationId: number): Promise<void> => {
  await apiClient.delete(`/clinic-invitations/${invitationId}/`);
};

// ============================================================================
// CLINIC WORKING DAYS
// ============================================================================

export interface WorkingDayListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ClinicWorkingDay[];
}

export const getClinicWorkingDays = async (
  clinicId: number,
  professionalId?: number
): Promise<WorkingDayListResponse> => {
  const params = new URLSearchParams();
  params.append('clinic', clinicId.toString());
  if (professionalId) params.append('professional', professionalId.toString());
  
  const response = await apiClient.get(`/clinic-working-days/?${params.toString()}`);
  return response.data;
};

export interface CreateWorkingDayData {
  clinic: number;
  professional: number;
  day_of_week: number;
  time_blocks?: Array<{
    start_time: string;
    end_time: string;
    appointment_duration: number;
  }>;
}

export const createClinicWorkingDay = async (data: CreateWorkingDayData): Promise<ClinicWorkingDay> => {
  const response = await apiClient.post('/clinic-working-days/', data);
  return response.data;
};

export const updateClinicWorkingDay = async (
  id: number,
  data: Partial<CreateWorkingDayData>
): Promise<ClinicWorkingDay> => {
  const response = await apiClient.patch(`/clinic-working-days/${id}/`, data);
  return response.data;
};

export const deleteClinicWorkingDay = async (id: number): Promise<void> => {
  await apiClient.delete(`/clinic-working-days/${id}/`);
};

// ============================================================================
// CLINIC TIME BLOCKS
// ============================================================================

export const createClinicTimeBlock = async (data: {
  working_day: number;
  start_time: string;
  end_time: string;
  appointment_duration: number;
}): Promise<ClinicTimeBlock> => {
  const response = await apiClient.post('/clinic-time-blocks/', data);
  return response.data;
};

export const updateClinicTimeBlock = async (
  id: number,
  data: Partial<{
    start_time: string;
    end_time: string;
    appointment_duration: number;
  }>
): Promise<ClinicTimeBlock> => {
  const response = await apiClient.patch(`/clinic-time-blocks/${id}/`, data);
  return response.data;
};

export const deleteClinicTimeBlock = async (id: number): Promise<void> => {
  await apiClient.delete(`/clinic-time-blocks/${id}/`);
};

// ============================================================================
// CLINIC BLOCKED DATES
// ============================================================================

export interface BlockedDateListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ClinicBlockedDate[];
}

export const getClinicBlockedDates = async (
  clinicId: number,
  professionalId?: number
): Promise<BlockedDateListResponse> => {
  const params = new URLSearchParams();
  params.append('clinic', clinicId.toString());
  if (professionalId) params.append('professional', professionalId.toString());
  
  const response = await apiClient.get(`/clinic-blocked-dates/?${params.toString()}`);
  return response.data;
};

export interface CreateBlockedDateData {
  clinic: number;
  professional: number;
  date: string;
  reason?: string;
  start_time?: string;
  end_time?: string;
}

export const createClinicBlockedDate = async (data: CreateBlockedDateData): Promise<ClinicBlockedDate> => {
  const response = await apiClient.post('/clinic-blocked-dates/', data);
  return response.data;
};

export const deleteClinicBlockedDate = async (id: number): Promise<void> => {
  await apiClient.delete(`/clinic-blocked-dates/${id}/`);
};

// ============================================================================
// CLINIC APPOINTMENTS
// ============================================================================

export interface AppointmentListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ClinicAppointment[];
}

export const getClinicAppointments = async (
  clinicId: number,
  options?: {
    professionalId?: number;
    date?: string;
    status?: string;
  }
): Promise<AppointmentListResponse> => {
  const params = new URLSearchParams();
  params.append('clinic', clinicId.toString());
  if (options?.professionalId) params.append('professional', options.professionalId.toString());
  if (options?.date) params.append('date', options.date);
  if (options?.status) params.append('status', options.status);
  
  const response = await apiClient.get(`/clinic-appointments/?${params.toString()}`);
  return response.data;
};

export const getClinicAppointmentById = async (id: number): Promise<ClinicAppointment> => {
  const response = await apiClient.get(`/clinic-appointments/${id}/`);
  return response.data;
};

export const getTodayClinicAppointments = async (): Promise<ClinicAppointment[]> => {
  const response = await apiClient.get('/clinic-appointments/today/');
  return response.data;
};

export interface CreateClinicAppointmentData {
  clinic: number;
  professional: number;
  patient?: number;
  guest_name?: string;
  guest_phone?: string;
  guest_email?: string;
  date: string;
  start_time: string;
  end_time: string;
  notes?: string;
  /** Client-generated id for idempotent offline replay -- see frontend/web/src/offline */
  client_uuid?: string;
}

export const createClinicAppointment = async (data: CreateClinicAppointmentData): Promise<ClinicAppointment> => {
  const response = await apiClient.post('/clinic-appointments/', data);
  return response.data;
};

export const updateClinicAppointment = async (
  id: number,
  data: { status?: string; notes?: string; arrived?: boolean }
): Promise<ClinicAppointment> => {
  const response = await apiClient.patch(`/clinic-appointments/${id}/`, data);
  return response.data;
};

// ============================================================================
// CLINIC SUBSCRIPTION
// ============================================================================

export type ClinicPlan = 'small' | 'medium' | 'large' | 'enterprise';

export interface ClinicSubscriptionData {
  subscription_plan: ClinicPlan;
  subscription_plan_display: string;
  subscription_price: number | null;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  subscription_custom_price: number | null;
  subscription_custom_max_attending: number | null;
  max_attending_allowed: number | null;
  attending_count: number;
  is_staff_viewer: boolean;
  paypal_subscription_status: 'pending' | 'active' | 'suspended' | 'cancelled' | 'expired' | null;
  pending_subscription_plan: ClinicPlan | null;
  pending_subscription_plan_display: string | null;
  pending_subscription_requested_at: string | null;
  /** Present only on a downgrade request with a live recurring PayPal
   * subscription - PayPal needs the buyer to re-approve the price change,
   * redirect here (see PayPalReturnPage's ?revision=1 handling). */
  approval_url?: string | null;
  plan_options: {
    plan: ClinicPlan;
    name: string;
    max_professionals: number | null;
    price: number | null;
  }[];
}

export interface ClinicPricingSettings {
  small_monthly_price: number;
  medium_monthly_price: number;
  large_monthly_price: number;
  small_max_attending: number;
  medium_max_attending: number;
  large_max_attending: number;
}

export const getClinicPricing = async (): Promise<ClinicPricingSettings> => {
  const response = await apiClient.get('/clinics/pricing/');
  return response.data;
};

export const getClinicSubscription = async (clinicId: number): Promise<ClinicSubscriptionData> => {
  const response = await apiClient.get(`/clinics/${clinicId}/subscription/`);
  return response.data;
};

export const updateClinicSubscription = async (
  clinicId: number,
  data: {
    subscription_plan?: ClinicPlan;
    subscription_start_date?: string | null;
    subscription_end_date?: string | null;
    subscription_custom_price?: number | null;
    subscription_custom_max_attending?: number | null;
  }
): Promise<ClinicSubscriptionData> => {
  const response = await apiClient.patch(`/clinics/${clinicId}/subscription/`, data);
  return response.data;
};

export const verifySubscriptionChange = async (clinicId: number): Promise<ClinicSubscriptionData> => {
  const response = await apiClient.post(`/clinics/${clinicId}/verify_subscription_change/`);
  return response.data;
};

export const rejectSubscriptionChange = async (clinicId: number): Promise<ClinicSubscriptionData> => {
  const response = await apiClient.post(`/clinics/${clinicId}/reject_subscription_change/`);
  return response.data;
};

export const cancelSubscriptionRequest = async (clinicId: number): Promise<ClinicSubscriptionData> => {
  const response = await apiClient.post(`/clinics/${clinicId}/cancel_subscription_request/`);
  return response.data;
};

export const confirmClinicAppointment = async (id: number): Promise<{ message: string }> => {
  const response = await apiClient.post(`/clinic-appointments/${id}/confirm/`);
  return response.data;
};

export const cancelClinicAppointment = async (id: number): Promise<{ message: string }> => {
  const response = await apiClient.post(`/clinic-appointments/${id}/cancel/`);
  return response.data;
};

export const markAppointmentArrived = async (id: number): Promise<{ message: string }> => {
  const response = await apiClient.post(`/clinic-appointments/${id}/mark_arrived/`);
  return response.data;
};

export const completeClinicAppointment = async (id: number): Promise<{ message: string }> => {
  const response = await apiClient.post(`/clinic-appointments/${id}/complete/`);
  return response.data;
};
